/**
 * Mandate Sidebar — navigation for the Mandate transition governance module.
 *
 * Follows PlanningSidebar.tsx pattern exactly.
 */

import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Target,
  ListOrdered,
  ScrollText,
  Users,
  Compass,
  Library,
  PlayCircle,
  Wrench,
  GitBranch,
  FileText,
} from 'lucide-react';
import { useProductSurface } from '../../hooks/useProductSurface';
import { setMandateShellActive } from '../../lib/productSurfacePaths';
import { SpaceSwitcher } from '../prioritization/SpaceSwitcher';
import { ProductModeSwitcher } from '../prioritization/ProductModeSwitcher';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { getDayNumber } from '../../types/mandate';

const mandateNav = [
  { name: 'Home', href: '/apps/mandate/dashboard', icon: LayoutDashboard },
  { name: 'Day 100 Outcome', href: '/apps/mandate/outcome', icon: Target },
  { name: 'Priorities Stack', href: '/apps/mandate/priorities', icon: ListOrdered },
  { name: 'Decision Record', href: '/apps/mandate/decisions', icon: ScrollText },
  { name: 'Stakeholders', href: '/apps/mandate/stakeholders', icon: Users },
  { name: 'Situation', href: '/apps/mandate/situation', icon: Compass },
  { name: 'Transition Record', href: '/apps/mandate/record', icon: FileText },
];

const workNav = [
  { name: 'Items', segment: '/items', icon: Library },
  { name: 'Sessions', segment: '/sessions', icon: PlayCircle },
  { name: 'Tools', segment: '/tools', icon: Wrench },
  { name: 'Lineage', segment: '/lineage', icon: GitBranch },
] as const;

export function MandateSidebar() {
  const location = useLocation();
  const { transition, currentOutcome } = useMandateStore();
  const { to } = useProductSurface();

  const isActive = (href: string) => location.pathname.startsWith(href);

  const dayNumber = transition?.startDate ? getDayNumber(transition.startDate) : null;
  const progressPct = dayNumber !== null ? Math.min((dayNumber / 100) * 100, 100) : 0;

  const outcomeVersion = currentOutcome?.version ?? 1;

  return (
    <div className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-[var(--rule)] bg-[var(--bg-raised)] transition-colors duration-200">
      {/* Logo */}
      <div className="flex h-14 shrink-0 items-center border-b border-[var(--rule)] px-5">
        <Link to="/apps/mandate/dashboard" className="flex items-center gap-2.5 leading-none">
          <svg height="24" width="auto" viewBox="0 0 142.85 94.47" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="shrink-0">
            <rect fill="#b8bccb" x="0"     y="82.27" width="142.85" height="12.2" />
            <rect fill="#9ba4be" x="15.02" y="65.76" width="112.82" height="12.2" />
            <rect fill="#8d9cc0" x="28.15" y="49.25" width="86.54"  height="12.2" />
            <rect fill="#7b8db8" x="39.42" y="32.73" width="64.02"  height="12.2" />
            <rect fill="#6b7faa" x="48.8"  y="16.22" width="45.25"  height="12.2" />
            <rect fill="#5a6f99" x="55.4"  y="0"     width="32.34"  height="11.61" />
          </svg>
          <span className="font-display text-[1.125rem] font-normal tracking-tight text-[var(--text-pri)] leading-none">
            Mandate
          </span>
        </Link>
      </div>

      <ProductModeSwitcher />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {/* Section label */}
        <div className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider px-3 pb-1 pt-3">
          Mandate
        </div>

        {/* Day counter */}
        <div className="mx-3 mb-3 mt-1 border border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
          {dayNumber !== null ? (
            <>
              <div className="text-sm font-semibold text-[var(--text-pri)]">
                Day {dayNumber} of 100
              </div>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-[var(--hover-tint)]">
                <div
                  className="h-1.5 rounded-full bg-primary-500 transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </>
          ) : (
            <div className="text-sm text-[var(--text-ter)]">Not started</div>
          )}
        </div>

        <div className="space-y-0.5">
          {mandateNav.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5  text-sm font-medium transition-all ${
                  active
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                <item.icon
                  className={`h-5 w-5 ${active ? 'text-primary-600' : 'text-[var(--text-ter)]'}`}
                />
                <span>{item.name}</span>
                {/* Version badge on Outcome item */}
                {item.href === '/apps/mandate/outcome' && outcomeVersion > 1 && (
                  <span className="ml-auto text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium px-1.5 py-0.5 rounded">
                    v{outcomeVersion}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        <div className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider px-3 pb-1 pt-5">
          Your work
        </div>
        <div className="space-y-0.5">
          {workNav.map((item) => {
            const href = to(item.segment);
            const active = location.pathname.startsWith(href);
            return (
              <Link
                key={item.segment}
                to={href}
                className={`flex items-center gap-3 px-3 py-2  text-sm font-medium transition-all ${
                  active
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                <item.icon
                  className={`h-4 w-4 ${active ? 'text-primary-600' : 'text-[var(--text-ter)]'}`}
                />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Footer */}
      <div className="border-t border-[var(--rule)] p-3 space-y-2">
        <SpaceSwitcher />
        <Link
          to="/prioritization/dashboard"
          onClick={() => setMandateShellActive(false)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
        >
          <Target className="h-4 w-4" />
          Open full Priorities
        </Link>
      </div>
    </div>
  );
}
